import { useState, useEffect, useRef } from 'react'
import { X, Check } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useWedding } from '../context/WeddingContext'
import { supabase } from '../lib/supabase'

// ── Pricing per region ────────────────────────────────────────────
// NOTE: every price/currency here MUST exist in verify-payment's PREMIUM_PRICES,
// or the payment verifies on the provider side but fails server-side → the couple
// pays and stays free. (The old GHS/KES "$39 USD" tiers did exactly that, because
// the server only honours USD at $89 — they've been removed.)
export const PRICING = {
  NGN: { label:'Nigeria',        symbol:'₦', amount:49900, currency:'NGN', provider:'paystack' },
  GBP: { label:'United Kingdom', symbol:'£', amount:69,    currency:'GBP', provider:'flutterwave' },
  USD: { label:'USA / Canada',   symbol:'$', amount:89,    currency:'USD', provider:'flutterwave' },
  EUR: { label:'Europe',         symbol:'€', amount:79,    currency:'EUR', provider:'flutterwave' },
}

function detectCurrency() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  if (tz.includes('Lagos') || tz.includes('Abuja'))  return 'NGN'
  if (tz.includes('London'))                          return 'GBP'
  if (tz.includes('Europe'))                          return 'EUR'
  return 'USD'
}

export default function UpgradeToPremium({ buttonLabel = 'Upgrade to Premium', onSuccess }) {
  const { user, refreshProfile }    = useAuth()

  // PricingPage is wrapped in WeddingProvider (App.jsx), so wedding is always
  // available whether the user is on the public pricing page or the dashboard.
  const { wedding, refreshWedding, loadingWedding } = useWedding()

  const [open, setOpen]                   = useState(false)
  const [currency, setCurrency]           = useState(detectCurrency)
  const [step, setStep]                   = useState('checkout')
  const [errorMsg, setErrorMsg]           = useState('')
  // FIX 1 — ref tracks current step so Paystack/Flutterwave callbacks
  // never read a stale value from the closure formed at openIframe() time
  const stepRef    = useRef(step)
  useEffect(() => { stepRef.current = step }, [step])

  const plan            = PRICING[currency]
  const isNGN           = currency === 'NGN'

  if (wedding?.plan === 'premium') {
    return (
      <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'#3D8B5E', borderRadius:100, padding:'0.5rem 1.125rem' }}>
        <Check size={14} color="white"/>
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'white' }}>Premium Active</span>
      </div>
    )
  }

  // ── Paystack ──────────────────────────────────────────────────
  function openPaystack() {
    if (!window.PaystackPop) {
      setErrorMsg('Payment script not loaded. Please refresh and try again.')
      return
    }
    const ref = `okoiya_${Date.now()}_${Math.random().toString(36).slice(2,8)}`
    const handler = window.PaystackPop.setup({
      key:      import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
      email:    user.email,
      amount:   plan.amount * 100, // kobo
      currency: 'NGN',
      ref,
      metadata: {
        wedding_id:    wedding.id,
        custom_fields: [{ display_name:'Product', variable_name:'product', value:'Okoiya Premium' }],
      },
      label: `${wedding.partner1_name} & ${wedding.partner2_name} — Okoiya Premium`,
      callback: (response) => {
        setStep('processing')
        verifyPayment({ provider:'paystack', reference:response.reference })
      },
      // FIX 1 applied — stepRef never stale
      onClose: () => {
        if (stepRef.current !== 'success' && stepRef.current !== 'processing') setStep('checkout')
      },
    })
    handler.openIframe()
  }

  // ── Flutterwave ───────────────────────────────────────────────
  function openFlutterwave() {
    if (!window.FlutterwaveCheckout) {
      setErrorMsg('Payment script not loaded. Please refresh and try again.')
      return
    }
    const txRef = `okoiya_flw_${Date.now()}_${Math.random().toString(36).slice(2,8)}`
    window.FlutterwaveCheckout({
      public_key:      import.meta.env.VITE_FLW_PUBLIC_KEY,
      tx_ref:          txRef,
      amount:          plan.amount,
      currency:        plan.currency,
      payment_options: 'card,banktransfer',
      customer: {
        email: user.email,
        name: `${wedding.partner1_name} & ${wedding.partner2_name}`,
      },
      customizations: {
        title:       'Okoiya Premium',
        description: 'One-time payment — full access forever',
        logo:        `${window.location.origin}/icon-512.png`,
      },
      callback: (data) => {
        if (data.status === 'successful' || data.status === 'completed') {
          setStep('processing')
          verifyPayment({ provider:'flutterwave', transaction_id:data.transaction_id })
        }
      },
      // FIX 1 applied
      onclose: () => {
        if (stepRef.current !== 'success' && stepRef.current !== 'processing') setStep('checkout')
      },
    })
  }

  // ── Verify with edge function ─────────────────────────────────
  async function verifyPayment(payload) {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { ...payload, wedding_id:wedding.id },
      })
      // FIX 3 — check error.message (network/edge errors) as well as data.error (logic errors)
      if (error || !data?.verified) {
        throw new Error(error?.message || data?.error || 'Verification failed. Email support@okoiya.com with your receipt.')
      }
      await refreshWedding()
      await refreshProfile()
      setStep('success')
      onSuccess?.()
    } catch (err) {
      setErrorMsg(err.message)
      setStep('error')
    }
  }

  function handlePay() {
    setErrorMsg('')
    if (!user)          { window.location.href = '/login?redirect=/pricing'; return }
    if (loadingWedding) { return } // wedding still fetching — button disabled anyway
    if (!wedding?.id)   { window.location.href = '/onboarding'; return }
    if (isNGN) openPaystack()
    else        openFlutterwave()
  }

  return (
    <>
      {/* Trigger button — FIX 2: always white+terracotta, never invisible */}
      <button
        onClick={() => { setStep('checkout'); setOpen(true) }}
        style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#C4826A', background:'white', border:'none', borderRadius:100, padding:'0.875rem', cursor:'pointer' }}>
        {buttonLabel}
      </button>

      {open && (
        <div onClick={e => { if (e.target===e.currentTarget) setOpen(false) }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'1rem' }}>

          <div style={{ background:'white', borderRadius:'1.5rem', width:'100%', maxWidth:440, padding:'2rem', position:'relative', boxShadow:'0 24px 64px rgba(0,0,0,0.2)', maxHeight:'90vh', overflowY:'auto' }}>
            <button onClick={() => setOpen(false)} style={{ position:'absolute', top:16, right:16, background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', padding:4, lineHeight:0 }}>
              <X size={18}/>
            </button>

            {step==='success' && (
              <div style={{ textAlign:'center', padding:'1rem 0' }}>
                <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(155,175,155,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.25rem' }}>
                  <Check size={32} color="#9BAF9B"/>
                </div>
                <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.6rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>You're Premium!</h2>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', color:'#6B6B6B', margin:'0 0 1.5rem', lineHeight:1.6 }}>
                  Every feature is now unlocked — forever. Go plan something beautiful.
                </p>
                <button onClick={() => setOpen(false)}
                  style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.875rem', cursor:'pointer' }}>
                  Continue →
                </button>
              </div>
            )}

            {step==='processing' && (
              <div style={{ textAlign:'center', padding:'2rem 0' }}>
                <div style={{ width:40, height:40, border:'3px solid #E5E0D8', borderTopColor:'#C4826A', borderRadius:'50%', animation:'okoiyaSpin 1s linear infinite', margin:'0 auto 1.25rem' }}/>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.95rem', color:'#6B6B6B', margin:0 }}>Verifying your payment…</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#BCBCBC', marginTop:'0.4rem' }}>Please do not close this window</p>
              </div>
            )}

            {step==='error' && (
              <div style={{ textAlign:'center', padding:'1rem 0' }}>
                <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#DC2626', margin:'0 0 0.75rem' }}>Something went wrong</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#6B6B6B', margin:'0 0 1.5rem', lineHeight:1.6 }}>{errorMsg}</p>
                <button onClick={() => setStep('checkout')}
                  style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#C4826A', background:'none', border:'1.5px solid #C4826A', borderRadius:100, padding:'0.625rem 1.5rem', cursor:'pointer' }}>
                  Try Again
                </button>
              </div>
            )}

            {step==='checkout' && (
              <>
                <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.5rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>Upgrade to Premium</h2>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:'0 0 1.5rem' }}>One-time payment. Everything unlocked. Forever.</p>



                {/* Price summary */}
                <div style={{ background:'#F8F5F2', borderRadius:'1rem', padding:'1rem 1.125rem', marginBottom:'1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0 }}>Total — one-time payment</p>
                    <p style={{ fontFamily:'Gibs, serif', fontSize:'1.5rem', color:'#1A1A1A', margin:0, fontWeight:400 }}>
                      {plan.symbol}{plan.amount.toLocaleString()}
                    </p>
                  </div>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC' }}>{plan.currency} · {plan.provider==='paystack'?'Paystack':'Flutterwave'}</span>
                </div>

                {errorMsg && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#DC2626', marginBottom:'0.75rem', lineHeight:1.5 }}>{errorMsg}</p>}

                <button onClick={handlePay}
                  disabled={loadingWedding}
                  style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.95rem', color:'white', background: loadingWedding ? '#D4BEB8' : 'linear-gradient(135deg,#C4826A,#D4A882)', border:'none', borderRadius:100, padding:'0.95rem', cursor: loadingWedding ? 'default' : 'pointer', marginBottom:'0.75rem', transition:'background 0.2s' }}>
                  {loadingWedding ? 'Loading…' : `Pay ${plan.symbol}${plan.amount.toLocaleString()} →`}
                </button>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', textAlign:'center', margin:0, lineHeight:1.6 }}>
                  Secured by {isNGN?'Paystack':'Flutterwave'} · No subscription · All sales final
                </p>
              </>
            )}
          </div>
        </div>
      )}
      <style>{`@keyframes okoiyaSpin{to{transform:rotate(360deg)}}`}</style>
    </>
  )
}
